import { useState, useEffect } from "react";
import { PlatformHead } from "../../layout/PlatformHead";
import Breadcrumbs from "@demystdata/ui/breadcrumbs";
import InputField from "@demystdata/ui/input_field";
import TextareaField from "@demystdata/ui/textarea_field";
import Tooltip from "@demystdata/ui/tooltip";
import { linksConstants } from "../../../constants/linksConstants";
import { FiEdit2, FiSave } from "react-icons/fi";
import { ConnectorCard } from "./CreateDataCatalog/ConnectorCard";
import { AddConnectorCard } from "./CreateDataCatalog/AddConnectorCard";
import Button from "@demystdata/ui/button";
import ClickAwayListener from "react-click-away-listener";
import { AnimatePresence, motion } from "framer-motion";
import { productsCatalogApi } from "../../../api/productsCatalogApi";
import { CloneAPIModal } from "../DataCatalog/components/dataPages/CloneAPIModal";

export const CreateDataAPI = () => {
  const [title, setTitle] = useState("");
  const [titleEdited, setTitleEdited] = useState(false);
  const [description, setDescription] = useState("");
  const [descriptionEdited, setDescriptionEdited] = useState(false);
  const [connectors, setConnectors] = useState([]);
  const [cloneAPIModalOpen, setCloneAPIModalOpen] = useState(false);

  const breadCrumbItems = [
    { href: linksConstants.ALL_USE_CASES, label: "Data APIs" },
    { href: null, label: "New Data API" },
  ];

  const handleKeyDown = e => {
    if (e.code === "Enter") {
      if (titleEdited === true) setTitleEdited(false);
      if (descriptionEdited === true) setDescriptionEdited(false);
    }
  };

  const connectorSelected = async connector => {
    const connectorsCopy = [...connectors];
    const { data } = await productsCatalogApi.getProduct(connector.id);
    connectorsCopy.push(data);
    setConnectors(connectorsCopy);
  };

  const connectorRemoved = id => {
    const connectorsCopy = [...connectors];
    const filteredItem = connectorsCopy.filter(item => item.id !== id);
    setConnectors(filteredItem);
  };

  const openCloneAPIModal = () => {
    setCloneAPIModalOpen(true);
  };

  const closeCloneAPIModal = () => {
    setCloneAPIModalOpen(false);
  };

  const saveDataAPI = () => {
    openCloneAPIModal();
  };

  useEffect(() => {
    if (titleEdited === true) {
      document.getElementById("title-input")?.focus();
    }
    if (descriptionEdited === true) {
      document.getElementById("description-input")?.focus();
    }
  }, [titleEdited, descriptionEdited]);

  useEffect(() => {
    document.addEventListener("keydown", e => handleKeyDown(e));

    return () => {
      document.removeEventListener("keydown", e => handleKeyDown(e));
    };
  }, [titleEdited, descriptionEdited]);

  return (
    <div className="CreateDataAPI">
      <PlatformHead title="Create Data API" />
      <CloneAPIModal
        open={cloneAPIModalOpen}
        closeModal={closeCloneAPIModal}
        variant="FROM_CREATE_DATA_API"
        id={69}
        name={title}
        description={description}
        connectors={connectors}
      />
      <main className="CreateDataAPI__contentWrapper">
        <section className="CreateDataAPI__breadcrumbs">
          <Breadcrumbs items={breadCrumbItems} variant="tertiary" />
        </section>
        <section className="CreateDataAPI__nameSection">
          <section className="CreateDataAPI__nameSection__title">
            {!titleEdited && (
              <>
                {title === "" ? (
                  <p className="CreateDataAPI__nameSection__title__text">Untitled Data API</p>
                ) : (
                  <Tooltip ariaLabel="ARIA label" label={title}>
                    <p className="CreateDataAPI__nameSection__title__text">{title}</p>
                  </Tooltip>
                )}
                <Button
                  variant="tertiary"
                  onClick={() => setTitleEdited(true)}
                  className="CreateDataAPI__nameSection__title__button"
                >
                  <FiEdit2 />
                </Button>
              </>
            )}
            {titleEdited && (
              <AnimatePresence>
                <ClickAwayListener onClickAway={() => setTitleEdited(false)}>
                  <motion.div>
                    <InputField
                      label=""
                      id="title-input"
                      autoComplete="off"
                      name="input"
                      onChange={e => setTitle(e)}
                      placeholder="Start typing Data API name..."
                      hideOptional={true}
                      required={false}
                      value={title}
                      className="CreateDataAPI__nameSection__title__input"
                      type="text"
                    />
                  </motion.div>
                </ClickAwayListener>
              </AnimatePresence>
            )}
            {connectors.length === 0 && (
              <Tooltip
                ariaLabel="You need connectors in your Data API to continue."
                color="blue"
                label="You need connectors in your Data API to continue."
              >
                <Button
                  variant="primary"
                  leftIconSrc={<FiSave />}
                  disabled
                  onClick={saveDataAPI}
                  className="CreateDataAPI__nameSection__title__saveButton"
                >
                  {`Save & Create`}
                </Button>
              </Tooltip>
            )}
            {connectors.length !== 0 && (
              <Button
                variant="primary"
                leftIconSrc={<FiSave />}
                onClick={saveDataAPI}
                className="CreateDataAPI__nameSection__title__saveButton"
              >
                {`Save & Create`}
              </Button>
            )}
          </section>
          <section className="CreateDataAPI__nameSection__description">
            {!descriptionEdited && (
              <button
                className="CreateDataAPI__nameSection__description__button"
                onClick={() => setDescriptionEdited(true)}
              >
                <p className="CreateDataAPI__nameSection__description__text">
                  {description === "" ? "Click here to enter description..." : description}
                </p>
              </button>
            )}
            {descriptionEdited && (
              <AnimatePresence>
                <ClickAwayListener onClickAway={() => setDescriptionEdited(false)}>
                  <motion.div>
                    <TextareaField
                      label=""
                      id="description-input"
                      name="textarea"
                      onChange={e => setDescription(e)}
                      placeholder="Enter description..."
                      hideOptional={true}
                      required={false}
                      value={description}
                      className="CreateDataAPI__nameSection__description__textarea"
                    />
                  </motion.div>
                </ClickAwayListener>
              </AnimatePresence>
            )}
          </section>
          {connectors.length === 0 && (
            <Tooltip
              ariaLabel="You need connectors in your Data API to continue."
              color="blue"
              label="You need connectors in your Data API to continue."
            >
              <Button
                variant="primary"
                leftIconSrc={<FiSave />}
                disabled
                onClick={saveDataAPI}
                className="CreateDataAPI__nameSection__saveButton"
              >
                {`Save & Create`}
              </Button>
            </Tooltip>
          )}

          {connectors.length !== 0 && (
            <Button
              variant="primary"
              leftIconSrc={<FiSave />}
              onClick={saveDataAPI}
              className="CreateDataAPI__nameSection__saveButton"
            >
              {`Save & Create`}
            </Button>
          )}
        </section>
        <section className="CreateDataAPI__connectorSection">
          <p className="CreateDataAPI__connectorSection__text">Select connectors</p>
          <section className="CreateDataAPI__connectorSection__table">
            {connectors.map(connector => (
              <ConnectorCard
                key={connector.id}
                data={connector}
                connectorRemoved={connectorRemoved}
              />
            ))}
            <AddConnectorCard
              connectors={connectors}
              setConnector={connectorSelected}
              isFirst={connectors.length === 0}
            />
          </section>
        </section>
      </main>
    </div>
  );
};
